import type { CSSProperties } from 'react';
import { getPasswordRequirementItems } from '../utils/password-policy';

export type PasswordRequirementsHintProps = {
  password: string;
  className?: string;
  style?: CSSProperties;
  /** When true, use colors suited for dark auth screens (white/emerald). */
  dark?: boolean;
};

/**
 * Live checklist for password strength rules.
 * Theme-aware via `dark` or default muted/foreground CSS variables.
 */
export function PasswordRequirementsHint({
  password,
  className,
  style,
  dark = false,
}: PasswordRequirementsHintProps) {
  const items = getPasswordRequirementItems(password);

  return (
    <ul
      className={className || 'mt-2 space-y-1.5 text-[11px]'}
      style={style}
      aria-live="polite"
    >
      {items.map((item) => {
        const okColor = dark ? 'text-emerald-400' : 'text-emerald-600 dark:text-emerald-400';
        const badColor = dark ? 'text-white/40' : 'text-[hsl(var(--muted-foreground))]';
        const okBadge = dark
          ? 'border-emerald-400/60 bg-emerald-500/20'
          : 'border-emerald-500/50 bg-emerald-500/15';
        const badBadge = dark ? 'border-white/20' : 'border-[hsl(var(--border))]';

        return (
          <li
            key={item.key}
            className={`flex items-center gap-2 ${item.ok ? okColor : badColor}`}
          >
            <span
              className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border text-[9px] ${
                item.ok ? okBadge : badBadge
              }`}
            >
              {item.ok ? '✓' : ''}
            </span>
            {item.label}
          </li>
        );
      })}
    </ul>
  );
}
